/**
 * captionsLanguages — language identity for the captions feature.
 *
 * The same language arrives in different spellings depending on the source:
 * the API projection uses `pt_BR` / `en` / `es`, HLS renditions carry BCP-47
 * tags (`pt-BR`, `en`, `es`), `users.language` historically holds lowercase
 * `pt_br`, and `navigator.language` can be anything (`pt`, `pt-BR`, `en-US`).
 * Every comparison must go through `langsMatch`, never a raw `===`.
 */

export const CAPTION_LANGUAGES = ["pt_BR", "en", "es"];

export const CAPTION_LANGUAGE_NAMES = {
    pt_BR: "Português",
    en: "English",
    es: "Español",
};

export function normalizeLang(tag) {
    if (!tag || typeof tag !== "string") return null;
    const normalized = tag.trim().toLowerCase().replace(/_/g, "-");
    return normalized || null;
}

function primarySubtag(tag) {
    const normalized = normalizeLang(tag);
    return normalized ? normalized.split("-")[0] : null;
}

/** `en` matches `en-US`, `pt-BR` matches `pt_BR`, etc. */
export function langsMatch(a, b) {
    const left = normalizeLang(a);
    const right = normalizeLang(b);
    if (!left || !right) return false;
    if (left === right) return true;
    return primarySubtag(left) === primarySubtag(right);
}

/** API/DB canonical code (`pt_BR`) for any loose tag; null when outside the trio. */
export function toCanonicalLang(tag) {
    return CAPTION_LANGUAGES.find((code) => langsMatch(code, tag)) ?? null;
}

/**
 * The viewer's language for the default-subtitle rule. `users.language` wins;
 * the browser locale is the guest/unset fallback. Null (unknown) means the
 * default rule stays off.
 */
export function viewerLanguage(user) {
    return toCanonicalLang(user?.language) ?? toCanonicalLang(navigator.language);
}
